import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Typography,
  Modal,
  IconButton,
  Divider,
  ListItemButton,
  ListItemText,
  List,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Close, ArrowBack } from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import {
  SIDEBAR_RAIL_WIDTH,
  SIDEBAR_WIDTH,
  TWO_LEVEL_PANEL_PX,
} from '../../sidebarMetrics';
import DynamicIcon from 'components/@2025/DynamicIcon';
import { AskAiPrompt, TopbarUtilities } from './components';
import { ASK_AI_PATH } from 'utils/askAiHandoff';
import { TOPBAR_PX } from '../../pageMetrics';

import useHandleTrackEvent from '@/hooks/useHandleTrackEvent';
import { updateUiState } from '@/redux/slices/uiSlice';

const Topbar = ({ onSidebarOpen, twoLevel = false }) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const currentContextData = useSelector(
    (state) => state.uiStates.currentContextData,
  );
  const showBackButtonOnTopbar = useSelector(
    (state) => state.uiStates.showBackButtonOnTopbar,
  );
  const currentNavigationLevel = useSelector(
    (state) => state.uiStates.currentNavigationLevel,
  );
  const showPlanExpired = useSelector(
    (state) => state.uiStates.showPlanExpired,
  );
  const showFeatureRestricted = useSelector(
    (state) => state.uiStates.showFeatureRestricted,
  );
  const currentTeamToManage = useSelector(
    (state) => state.uiStates.currentTeamToManage,
  );
  const showCreditsRequired = useSelector(
    (state) => state.uiStates.showCreditsRequired,
  );
  const [showRegistrationClosed, setShowRegistrationClosed] = useState(false);
  const [showContestInactive, setShowContestInactive] = useState(false);

  const openRegistrationClosed = Boolean(showRegistrationClosed);
  const openContestInactive = Boolean(showContestInactive);

  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const isViewAsMode = useSelector((state) => state.auth?.isViewAsMode);
  const dashboardSidebarExpanded = useSelector(
    (state) => state.uiStates.dashboardSidebarExpanded,
  );
  // Preferences can pin the sidebar collapsed, which drops the toggle.
  const navCollapsedByDefault = useSelector(
    (state) => state.uiStates.navCollapsedByDefault,
  );
  const showSidebarToggle = twoLevel && navCollapsedByDefault === false;

  const handleTrackEvent = useHandleTrackEvent();
  const iconsLibrary = useSelector((state) => state.uiStates.iconsLibrary);

  const [showProductHelp, setShowProductHelp] = useState(false);

  const [activeDemo, setActiveDemo] = useState(0);
  const [, setActiveStep] = useState('');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [demos] = useState([
    {
      title: 'Account Setup',
      expanded: true,
      activeStep: 0,
      description:
        'To derive the maximum advantages from your account, you need to set up a few things.<br /><br />Click <strong>Next</strong> to learn more.',
      imageFolder: 'accountsettings',
      numberOfSteps: 8,
      subSections: [
        'Settings - Roleplays',
        'Settings - Sales Call Analysis',
        'Settings - Playbook',
      ],
      steps: [
        {
          title: 'Account Settings > Playbook',
          subText:
            'Go to <strong>Account Settings > Playbook</strong> from the navigation panel. On this page you can configure settings for roleplays and sales call analysis.',
        },
        {
          title: 'Settings - Roleplays',
          subText:
            'Provide one or more Target Verticals for your sales prospecting.',
        },
        {
          title: 'Settings - Roleplays',
          subText: 'Provide one or more Designations of prospects you target.',
        },
        {
          title: 'Settings - Sales Call Analysis',
          subText:
            'Select the applicable sales methodologies for analysis. These will be used in addition to the playbook.',
        },
        {
          title: 'Settings - Sales Call Analysis',
          subText:
            'We recommend you leave Auto Analysis enabled so that call analysis can happen in the background for uploaded recordings.',
        },
        {
          title: 'Settings - Playbook',
          subText:
            'Click the <strong>Create Playbook</strong> button (at the bottom right) to create your sales playbook.',
        },
        {
          title: 'Settings - Playbook',
          subText:
            'Fill out the sections as per your sales process. This information will be used to analyze sales call recordings uploaded to your account.',
        },
      ],
    },
    {
      title: 'Team Management',
      expanded: true,
      activeStep: 0,
      description:
        'Team admins can invite team members to the platform, ensure central management of playbooks, sales methodologies, call analysis, roleplays, automations and reports so that all pieces are aligned for best team performance.<br /><br />Click <strong>Next</strong> to learn more.',
      imageFolder: 'manageteam',
      numberOfSteps: 6,
      subSections: ['Invite a Team Member', 'Manage Invitations'],
      steps: [
        {
          title: 'My Team > Manage Members',
          subText:
            'Go to <strong>My Team > Manage Members</strong> from the navigation panel. From this page you can add members to your team and manage them.',
        },
        {
          title: 'Invite a Team Member',
          subText: 'Go to <strong>Invite Members</strong> tab.',
        },
        {
          title: 'Invite a Team Member',
          subText: 'Provide name & email of the member and select their role.',
        },
        {
          title: 'Invite a Team Member',
          subText:
            'Click <strong>Send Invite</strong>. An email with the sign up link will be sent to the invited member.',
        },
        {
          title: 'Manage Invitations',
          subText:
            'Track sent invitations from the <strong>Invitations</strong> tab.',
        },
      ],
    },
    {
      title: 'Sales Call Analysis',
      expanded: true,
      activeStep: 0,
      description:
        'Sales call analysis highlights selling strengths and identifies areas of improvement.<br />A detailed report is created for each call recording.<br /><br />Click <strong>Next</strong> to learn more.',
      imageFolder: 'salescall',
      numberOfSteps: 15,
      subSections: ['Analyze a Sales Call', 'View Analysis Report'],
      steps: [
        {
          title: 'Sales Call Analysis > Upload Recording',
          subText:
            'Go to <strong>Sales Call Analysis > Upload Recording</strong> from the navigation panel. From this page you can upload call recordings and have them analyzed',
        },
        {
          title: 'Analyze a Sales Call',
          subText: 'Upload a call recording',
        },
        {
          title: 'Analyze a Sales Call',
          subText: 'Start Analysis - Click Next button',
        },
        {
          title: 'Analyze a Sales Call',
          subText: 'Analysis in Progress - In progess icon in Actions column',
        },
        {
          title: 'Analyze a Sales Call',
          subText: 'Analysis Complete - Report icon visible in Actions column',
        },
        {
          title: 'View Analysis Report',
          subText:
            'View Analysis - Click the Report icon in the Actions column',
        },
        {
          title: 'View Analysis Report',
          subText: 'Call Summary - Default Tab',
        },
        {
          title: 'View Analysis Report',
          subText: 'Deal Risk - Shown at the bottom of the Summary',
        },
        {
          title: 'View Analysis Report',
          subText: 'Playbook Alignment - Playbook Tab',
        },
        {
          title: 'View Analysis Report',
          subText:
            'Maximize View - Click the second icon from right in the tabs header',
        },
        {
          title: 'View Analysis Report',
          subText: 'Playbook Alignment Maximized View',
        },
        {
          title: 'View Analysis Report',
          subText:
            'Playbook Alignment - View Playbook in Sidebar - Click the book icon in the tabs header',
        },
        {
          title: 'View Analysis Report',
          subText:
            'Playbook Alignment Maximized View - Show Playbook in Sidebar',
        },
        {
          title: 'View Analysis Report',
          subText: 'Ask Donna - Ask AI any question about the call',
        },
      ],
    },
  ]);
  const [demoSteps, setDemoSteps] = useState([]);

  useEffect(() => {
    if (!showProductHelp) {
      setActiveDemo(0);
      setActiveStep('');
      setActiveStepIndex(0);
    }
  }, [showProductHelp]);

  useEffect(() => {
    setActiveStep(demos?.[activeDemo]?.steps?.[activeStepIndex - 1]?.title);
  }, [activeStepIndex, activeDemo]);

  // const handleProfileMenuClick = (event) => {
  //   if (anchorEl) setAnchorEl(null);
  //   else setAnchorEl(event.target);
  // };

  // const handleClose = () => {
  //   setAnchorEl(null);
  // };

  // const handleLogoutClick = () => {
  //   if (localStorage.getItem('notificationToken')) {
  //     dispatch(
  //       updateNotificationPreferences(
  //         {
  //           pushnotification: false,
  //         },
  //         () => {
  //           localStorage.removeItem('notificationToken');
  //           dispatch(
  //             firebaseLogout(() => {
  //               dispatch(firebaseCheckSession());
  //               history.go();
  //             }),
  //           );
  //         },
  //       ),
  //     );
  //   } else {
  //     dispatch(
  //       firebaseLogout(() => {
  //         dispatch(firebaseCheckSession());
  //         history.go();
  //       }),
  //     );
  //   }
  // };

  useEffect(() => {
    if (
      userDetails?.orgRoles?.[0] === 'ORG_ADMIN'
      // ||
      // userDetails?.orgRoles?.[0] === 'WL_ORG_ADMIN'
    ) {
      setDemoSteps([
        {
          title: 'Discovery Template',
          onBoardingStep: 'start',
          onBoardingFor: 'orgAdmin',
        },
        {
          title: 'Deals',
          onBoardingStep: 'deals',
          onBoardingFor: 'orgAdmin',
        },
      ]);
    } else if (userDetails?.teamRoles?.[0] === 'TEAM_ADMIN') {
      setDemoSteps([
        {
          title: 'Team Setup',
        },
        {
          title: 'Playbook Creation',
        },
        {
          title: 'Sales Call Analysis',
        },
        {
          title: 'Private Roleplays',
        },
        {
          title: 'Managing Team Members',
        },
        {
          title: 'Automatic Call Recordings',
        },
        {
          title: 'Automatic Call Analysis',
        },
      ]);
    } else if (userDetails?.teamRoles?.[0] === 'STANDARD') {
      setDemoSteps([
        {
          title: 'Sales Call Analysis',
        },
        {
          title: 'Private Roleplays',
        },
        {
          title: 'Automatic Call Recordings',
        },
        {
          title: 'Call Analysis & Roleplays Settings',
        },
      ]);
    } else {
      setDemoSteps([]);
    }
  }, [userDetails]);

  useEffect(() => {
    if (!showProductHelp) {
      setActiveDemo(0);
      setActiveStep('');
      setActiveStepIndex(0);
    }
  }, [showProductHelp]);

  useEffect(() => {
    setActiveStep(demos?.[activeDemo]?.steps?.[activeStepIndex - 1]?.title);
  }, [activeStepIndex, activeDemo]);

  // Ask AI is offered wherever the sidebar offers a new chat: admins, outside
  // View As. On the Ask AI page itself the page's own composer takes over, and
  // the field gives way to the way back to where the question was asked.
  const canAskAi =
    isLoggedIn &&
    !isViewAsMode &&
    (userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
      userDetails?.teamRoles?.[0] === 'TEAM_ADMIN');
  const onAskAiPage = location.pathname === ASK_AI_PATH;

  // How far the bar's first item sits from where the bar's content starts,
  // so it lines up with the page content's left edge: pages pad their content
  // 24px off the sidebar (16px below `md`), and the bar's content starts at a
  // different spot per layout.
  //   one-level      at the sidebar's edge (1-2px short when collapsed, from
  //                  the offsets below)
  //   two-level      after the toggle slot, which ends 8px short of the
  //                  panel's edge; collapsed, the toggle sits where the
  //                  content edge would be, so the field just follows it;
  //                  pinned collapsed (no toggle), the bar's own padding
  //                  already puts it at the content edge
  const pageInsetPx = isMd ? 40 : 16;
  const contentInsetPx = twoLevel
    ? !showSidebarToggle
      ? 0
      : dashboardSidebarExpanded
      ? pageInsetPx + 8
      : 16
    : dashboardSidebarExpanded
    ? pageInsetPx
    : pageInsetPx + (isMd ? 1 : 2);
  const askAiReturnTo = onAskAiPage ? location.state?.askAiReturnTo : null;

  return (
    <>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        // width={1}
        sx={{
          // The topbar is laid out inside a `Container` whose horizontal padding
          // differs by breakpoint, so these offsets are the sidebar width less
          // that padding rather than the sidebar width itself. Expressed as
          // deltas off the shared widths so a change to the sidebar's width --
          // or the two-level preference -- carries through here instead of
          // leaving the bar misaligned.
          // Two-level: the AppBar itself already starts at the rail's edge
          // and the bar spans the nav panel, so it just fills its container.
          width: twoLevel
            ? 1
            : dashboardSidebarExpanded
            ? `calc(100vw - ${SIDEBAR_WIDTH} - 15px)`
            : `calc(100vw - ${SIDEBAR_RAIL_WIDTH} + 1px)`,
          // The bar's share of `TOPBAR_PX`; its hairline and the divider under
          // it take the other 2px.
          minHeight: `${TOPBAR_PX - 2}px`,
          marginLeft: twoLevel
            ? 0
            : dashboardSidebarExpanded
            ? isMd
              ? `calc(${SIDEBAR_WIDTH} - 32px)`
              : `calc(${SIDEBAR_WIDTH} - 10px)`
            : isMd
            ? `calc(${SIDEBAR_RAIL_WIDTH} - 33px)`
            : `calc(${SIDEBAR_RAIL_WIDTH} - 10px)`,
          zIndex: 111111,
          // background: '#333',
        }}
      >
        {/*
          Two-level: the collapse/expand toggle lives on the topbar, over the
          nav panel, at the panel's top-right corner. The bar starts at the
          rail's edge, so a slot as wide as the panel (less the container's
          left padding) puts the toggle there. Collapsed, the slot shrinks to
          the toggle, which then sits beside the rail.
        */}
        {showSidebarToggle ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
              flexShrink: 0,
              width: dashboardSidebarExpanded
                ? `${TWO_LEVEL_PANEL_PX - (isMd ? 32 : 8) - 8}px`
                : 'auto',
              marginLeft: dashboardSidebarExpanded ? -5 : isMd ? -5 : 0,
            }}
          >
            <Tooltip
              title={
                dashboardSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'
              }
              arrow
              placement="bottom"
            >
              <IconButton
                data-tour="topbar-sidebar-toggle"
                data-trackid={'toggle_sidebar_expand'}
                aria-label={
                  dashboardSidebarExpanded
                    ? 'Collapse sidebar'
                    : 'Expand sidebar'
                }
                size="small"
                onClick={(e) => {
                  handleTrackEvent(e, 'toggle_sidebar_expand');
                  dispatch(
                    updateUiState({
                      key: 'dashboardSidebarExpanded',
                      value: !dashboardSidebarExpanded,
                    }),
                  );
                  // Remembered so a reload keeps the sidebar as it was left.
                  dispatch(
                    updateUiState({
                      key: 'navSidebarExpanded',
                      value: !dashboardSidebarExpanded,
                    }),
                  );
                }}
              >
                <DynamicIcon
                  type={iconsLibrary || 'lucide'}
                  name={
                    dashboardSidebarExpanded
                      ? 'toggleSidebarCollapse'
                      : 'toggleSidebarExpand'
                  }
                  color={theme.palette.text.secondary}
                  width={20}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            // Lines the bar's first item -- the Ask AI field -- up with the
            // left edge of the page content below it.
            paddingLeft: `${contentInsetPx}px`,
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          {canAskAi && !onAskAiPage ? (
            <Box
              sx={{
                flex: '0 1 640px',
                minWidth: 0,
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              <AskAiPrompt />
            </Box>
          ) : null}
          {askAiReturnTo?.path ? (
            <Button
              data-testid="topbar-ask-ai-back"
              data-trackid={'topbar_ask_ai_back'}
              variant="text"
              startIcon={<ArrowBack />}
              size="small"
              onClick={(e) => {
                handleTrackEvent(e, 'topbar_ask_ai_back');
                history.push(askAiReturnTo.path);
              }}
              sx={{
                color: 'text.primary',
                maxWidth: 1,
                minWidth: 0,
                '& .MuiButton-startIcon': { flexShrink: 0 },
              }}
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Back to {askAiReturnTo.label}
              </Box>
            </Button>
          ) : null}
          {showBackButtonOnTopbar || currentTeamToManage ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              sx={{
                marginLeft: 1,
              }}
              onClick={() => {
                if (currentTeamToManage) {
                  dispatch(
                    updateUiState({
                      key: 'currentTeamToManage',
                      value: null,
                    }),
                  );
                  history.push('/all-teams');
                }
                if (history?.location?.pathname === '/tasks') {
                  dispatch(
                    updateUiState({
                      key: 'showBackButtonOnTopbar',
                      value: false,
                    }),
                  );
                  history.goBack();
                }
                if (currentNavigationLevel > 2) {
                  dispatch(
                    updateUiState({
                      key: 'currentNavigationLevel',
                      value: currentNavigationLevel - 1,
                    }),
                  );
                  dispatch(
                    updateUiState({
                      key: 'currentContextData',
                      value: {
                        ...currentContextData,
                        title: '',
                      },
                    }),
                  );
                } else {
                  dispatch(
                    updateUiState({
                      key: 'currentNavigationLevel',
                      value:
                        currentNavigationLevel > 1
                          ? currentNavigationLevel - 1
                          : 1,
                    }),
                  );
                  dispatch(
                    updateUiState({
                      key: 'showBackButtonOnTopbar',
                      value: false,
                    }),
                  );
                  dispatch(
                    updateUiState({
                      key: 'currentContextData',
                      value: null,
                    }),
                  );
                }
              }}
              color="primary"
              size="small"
            >
              Go Back
            </Button>
          ) : null}
          {currentContextData &&
          Object.keys(currentContextData || {})?.length > 0 ? (
            <Box
              display={'flex'}
              flexDirection={'row'}
              gap={2}
              alignItems={'center'}
            >
              {currentContextData?.currentTeam ? (
                <Typography
                  variant="body1"
                  color={'text.secondary'}
                  fontWeight={700}
                >
                  {currentContextData?.currentTeam}
                </Typography>
              ) : null}
              {currentContextData?.name || currentContextData?.email ? (
                <>
                  {currentContextData?.currentTeam ? (
                    <Divider
                      orientation="vertical"
                      variant="fullWidth"
                      flexItem
                    />
                  ) : null}
                  <Box display={'flex'} flexDirection={'column'}>
                    {currentContextData?.name ? (
                      <Typography
                        variant="subtitle2"
                        color={'text.secondary'}
                        fontWeight={700}
                      >
                        {currentContextData?.name}
                      </Typography>
                    ) : null}
                    {currentContextData?.email ? (
                      <Typography
                        variant="caption"
                        color={'text.secondary'}
                        // fontWeight={700}
                      >
                        {currentContextData?.email}
                      </Typography>
                    ) : null}
                  </Box>
                </>
              ) : null}
              {currentContextData?.title ? (
                <>
                  {currentContextData?.currentTeam ||
                  currentContextData?.name ||
                  currentContextData?.email ? (
                    <Divider
                      orientation="vertical"
                      variant="fullWidth"
                      flexItem
                    />
                  ) : null}
                  <Typography
                    variant="body1"
                    color={'text.secondary'}
                    fontWeight={700}
                  >
                    {currentContextData?.title}
                  </Typography>
                </>
              ) : null}
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Box
            sx={{ display: { xs: 'flex', md: 'flex' } }}
            alignItems={'center'}
          >
            {isLoggedIn && userInfo ? (
              <TopbarUtilities />
            ) : (
              <Box marginLeft={4}>
                <Button
                  data-trackid={'cta_sign_in_sign_up'}
                  size={'small'}
                  variant="outlined"
                  fullWidth
                  component="a"
                  href="/auth/signin"
                  onClick={(e) => {
                    handleTrackEvent(e, 'cta_sign_in_sign_up');
                  }}
                >
                  Sign In / Sign Up
                </Button>
              </Box>
            )}
          </Box>
          <Box
            sx={{ display: { xs: 'block', sm: 'none', md: 'none' } }}
            alignItems={'center'}
          >
            <Button
              data-trackid={'toggle_sidebar'}
              onClick={(e) => {
                onSidebarOpen();
                handleTrackEvent(e, 'toggle_sidebar');
              }}
              aria-label="Menu"
              variant={'outlined'}
              sx={{
                borderRadius: 2,
                minWidth: 'auto',
                padding: 1,
                borderColor: alpha(theme.palette.divider, 0.2),
              }}
            >
              <MenuIcon />
            </Button>
          </Box>
        </Box>
      </Box>
      <Modal
        open={showCreditsRequired}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            dispatch(
              updateUiState({
                key: 'showCreditsRequired',
                value: false,
              }),
            );
          } else return;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            maxWidth: '450px',
            outline: 'none',
            borderRadius: 1,
            padding: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Credits Required</Typography>
            <IconButton
              onClick={() => {
                dispatch(
                  updateUiState({
                    key: 'showCreditsRequired',
                    value: false,
                  }),
                );
              }}
              aria-label="close"
            >
              <Close style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
          <Divider sx={{ width: 1 }} />
          <Box p={1}>
            <Typography variant="body1" mt={1}>
              {userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
              userDetails?.orgRoles?.[0] === 'WL_ORG_ADMIN'
                ? "You don't have sufficient credits to continue. Please buy more credits or upgrade your plan."
                : "You don't have sufficient credits to continue. Please alert your account admin to add more credits."}
            </Typography>
          </Box>
          {userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
          userDetails?.orgRoles?.[0] === 'WL_ORG_ADMIN' ? (
            <Box
              sx={{
                width: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
              // mt={2}
            >
              <Button
                variant="contained"
                data-trackid={'close_plan_expired_dialog'}
                color="primary"
                onClick={(e) => {
                  handleTrackEvent(e, 'close_plan_expired_dialog');
                  dispatch(
                    updateUiState({
                      key: 'showCreditsRequired',
                      value: false,
                    }),
                  );
                  history.push('/pricing');
                }}
              >
                See All Plans
              </Button>
            </Box>
          ) : null}
        </Box>
      </Modal>
      <Modal
        open={showPlanExpired}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown')
            dispatch(
              updateUiState({
                key: 'showPlanExpired',
                value: false,
              }),
            );
          else return;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            maxWidth: '450px',
            outline: 'none',
            borderRadius: 1,
            padding: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Your current plan has expired.</Typography>
            <IconButton
              data-trackid={'close_plan_expired_dialog'}
              onClick={(e) => {
                handleTrackEvent(e, 'close_plan_expired_dialog');
                dispatch(
                  updateUiState({
                    key: 'showPlanExpired',
                    value: false,
                  }),
                );
              }}
              aria-label="close"
            >
              <Close style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
          <Divider sx={{ width: 1 }} />
          <Box p={1}>
            <Typography variant="body1" mt={1}>
              Please select a plan from the Plans page, click Contact Support
              and we will reach out to you promptly.
            </Typography>
          </Box>
          <Box
            sx={{
              width: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
            mt={2}
          >
            <Button
              variant="contained"
              data-trackid={'close_plan_expired_dialog'}
              color="primary"
              onClick={(e) => {
                handleTrackEvent(e, 'close_plan_expired_dialog');
                dispatch(
                  updateUiState({
                    key: 'showPlanExpired',
                    value: false,
                  }),
                );
                history.push('/pricing');
              }}
            >
              See All Plans
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={showFeatureRestricted}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown')
            dispatch(
              updateUiState({
                key: 'showFeatureRestricted',
                value: false,
              }),
            );
          else return;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            maxWidth: '450px',
            outline: 'none',
            borderRadius: 1,
            padding: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Restricted</Typography>
            <IconButton
              data-trackid={'close_plan_expired_dialog'}
              onClick={(e) => {
                handleTrackEvent(e, 'close_plan_expired_dialog');
                dispatch(
                  updateUiState({
                    key: 'showFeatureRestricted',
                    value: false,
                  }),
                );
              }}
              aria-label="close"
            >
              <Close style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
          <Divider sx={{ width: 1 }} />
          <Box p={1}>
            <Typography variant="body1" mt={1}>
              Google Calendar integration lets you send your personal Meetbot to
              record your meetings and have them automatically analyzed. Please
              get in touch with your account administrator to have it added to
              your plan.
            </Typography>
          </Box>
          {/* <Box
            sx={{
              width: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
            mt={2}
          >
            <Button
              variant="contained"
              data-trackid={'close_plan_expired_dialog'}
              color="primary"
              onClick={(e) => {
                handleTrackEvent(e, 'close_plan_expired_dialog');
                dispatch(
                  updateUiState({
                    key: 'showFeatureRestricted',
                    value: false,
                  }),
                );
                history.push('/pricing');
              }}
            >
              See All Plans
            </Button>
          </Box> */}
        </Box>
      </Modal>
      <Modal
        open={openContestInactive}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown')
            setShowContestInactive(false);
          else return;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            maxWidth: '450px',
            outline: 'none',
            borderRadius: 1,
            padding: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Contest is not live.</Typography>
            <IconButton
              data-trackid={'close_contest_inactive_dialog'}
              onClick={(e) => {
                handleTrackEvent(e, 'close_contest_inactive_dialog');
                setShowContestInactive(false);
              }}
              aria-label="close"
            >
              <Close style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
          <Divider sx={{ width: 1 }} />
          <Box p={1}>
            <Typography variant="body1" mt={1}>
              Oops! This contest has ended.
            </Typography>
            <Typography variant="body1" mt={1}>
              You can try participating in other live contests.
            </Typography>
          </Box>
          <Box
            sx={{
              width: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
            mt={2}
          >
            <Button
              variant="contained"
              data-trackid={'contest_inactive_dialog_closed'}
              color="primary"
              onClick={(e) => {
                handleTrackEvent(e, 'contest_inactive_dialog_closed');
                setShowRegistrationClosed(false);
                history.push('/contests');
              }}
            >
              Go to Contests
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={openRegistrationClosed}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown')
            setShowRegistrationClosed(false);
          else return;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            maxWidth: '450px',
            outline: 'none',
            borderRadius: 1,
            padding: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Registration Closed!</Typography>
            <IconButton
              data-trackid={'close_save_to_calendar_dialog'}
              onClick={(e) => {
                handleTrackEvent(e, 'close_save_to_calendar_dialog');
                setShowRegistrationClosed(false);
              }}
              aria-label="close"
            >
              <Close style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
          <Divider sx={{ width: 1 }} />
          <Box p={1}>
            <Typography variant="body1" mt={1}>
              Oops, this contest is not accepting new entries at this point as
              it is packed to capacity. We’ve added you to the waitlist and will
              let you know as soon as we can accommodate you.
            </Typography>
            <Typography variant="body1" mt={1}>
              In the meantime you can hone your skills with one of our practice
              contests.
            </Typography>
          </Box>
          <Box
            sx={{
              width: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
            mt={2}
          >
            <Button
              variant="contained"
              data-trackid={'registration_closed_dialog_close'}
              color="primary"
              onClick={(e) => {
                handleTrackEvent(e, 'registration_closed_dialog_close');
                setShowRegistrationClosed(false);
                history.push('/practice');
              }}
            >
              Go to Practice
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={showProductHelp}
        onClose={() => {
          setShowProductHelp(false);
          // if (reason !== 'backdropClick') setShowProductHelp(false);
          // else return;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            maxWidth: '90vw',
            minWidth: '90vw',
            outline: 'none',
            borderRadius: 1,
            // padding: 2,
            position: 'relative',
          }}
        >
          <Tooltip title="Close" placement="top">
            <IconButton
              data-trackid={'close_product_help_dialog'}
              onClick={(e) => {
                handleTrackEvent(e, 'close_product_help_dialog');
                setShowProductHelp(false);
              }}
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                background: 'var(--dc-border)',
                ':hover': {
                  background: 'var(--dc-surface-raised)',
                },
                padding: '4px',
              }}
              aria-label="close"
            >
              <Close style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Box display={'flex'}>
            <Box
              flex={1}
              sx={{
                minHeight: '90vh',
                maxHeight: '90vh',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '5px',
                },
                '&::-webkit-scrollbar-track': {
                  boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                  webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'var(--dc-scrollbar)',
                  borderRadius: 2,
                },
                borderRight: '1px solid var(--dc-border)',
              }}
            >
              <Typography
                variant="body1"
                color={'text.secondary'}
                sx={{
                  display: 'block',
                  padding: 2,
                  paddingBottom: 1,
                  fontWeight: 700,
                  borderBottom: '1px solid var(--dc-border)',
                }}
              >
                Deal Copilot Product Help
              </Typography>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <List
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2,
                  }}
                >
                  {demoSteps?.map((demo, index) => (
                    <Box
                      key={index}
                      sx={{
                        border: '1px solid var(--dc-border)',
                        borderRadius: '4px',
                      }}
                    >
                      <ListItemButton
                        selected={activeDemo === index}
                        onClick={() => {
                          setActiveDemo(index);
                          setActiveStepIndex(0);
                          setActiveStep('');
                        }}
                      >
                        <ListItemText
                          primary={demo.title}
                          sx={{
                            '& .MuiTypography-root': {
                              fontWeight: 700,
                            },
                          }}
                        />
                      </ListItemButton>
                    </Box>
                  ))}
                </List>
              </Box>
            </Box>
            <Box
              flex={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: 1,
              }}
            >
              <Box sx={{ minHeight: 'calc(90vh - 80px)' }}></Box>
              <Box
                sx={{
                  borderTop: '1px solid var(--dc-border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 16px',
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={activeDemo === 0}
                  onClick={() => setActiveDemo(activeDemo - 1)}
                >
                  Prev
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={activeDemo === demoSteps.length - 1}
                    onClick={() => setActiveDemo(activeDemo + 1)}
                  >
                    Next
                  </Button>
                  <Button
                    id={`start_onboarding_demo${activeDemo}_for_${
                      userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
                      userDetails?.orgRoles?.[0] === 'WL_ORG_ADMIN'
                        ? 'org_admin'
                        : userDetails?.teamRoles?.[0] === 'TEAM_ADMIN'
                        ? 'team_admin'
                        : 'standard'
                    }`}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      dispatch(
                        updateUiState({
                          key: 'onboardingSettings',
                          value: {
                            onBoarding: true,
                            onBoardingFor:
                              demoSteps?.[activeDemo]?.onBoardingFor,
                            onBoardingStep:
                              demoSteps?.[activeDemo]?.onBoardingStep,
                          },
                        }),
                      );
                      dispatch({ type: 'RESTART_TOUR' });
                      setShowProductHelp(false);
                    }}
                  >
                    Start Onboarding Demo
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

Topbar.propTypes = {
  onSidebarOpen: PropTypes.func,
  colorInvert: PropTypes.bool,
  setPage: PropTypes.func,
  twoLevel: PropTypes.bool,
};

export default Topbar;
